import {
  Psychology as AnalyzerIcon,
  CheckCircle as CheckIcon,
  Edit as EditIcon,
  Splitscreen as SplitIcon
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { Page } from '@/components/page';
import { useSceneSnapshot } from '@/features/scenes/hooks/use-scene-snapshot';
import { useFirebase } from '@/firebase';
import { useNarrativeItem } from '@/hooks/use-entity';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useSubscription } from '@/hooks/useSubscription';
import type { AnySummary, EntitySummary, EventSummary, NarrativeSummary, Scene } from '@/lib/types';

import { ProposedSplitDialog } from '@/features/scenes/components/ProposedSplitDialog';
import { SceneDetailsCard } from '@/features/scenes/components/SceneDetailsCard';

export const Route = createFileRoute('/scenes/view/$id')({
  component: SceneViewPage,
});

function SceneViewContent() {
  const { id: sceneId } = Route.useParams();
  const { user, backend } = useFirebase();
  const { novel, getItemRef, getSummaries } = useNovelRepository();
  const novelId = novel?.id;

  // Entity data from summaries using type-safe getter
  const characterArray = getSummaries<EntitySummary>('characters');
  const locationArray = getSummaries<EntitySummary>('locations');
  const organizationArray = getSummaries<EntitySummary>('organizations');
  const eventArray = getSummaries<EventSummary>('events');
  const sceneArray = getSummaries<NarrativeSummary>('scenes');

  // Convert to lookup maps for easy ID-based access in the UI
  const characters = useMemo(() => Object.fromEntries(characterArray.map(c => [c.id, c])), [characterArray]);
  const locations = useMemo(() => Object.fromEntries(locationArray.map(l => [l.id, l])), [locationArray]);
  const organizations = useMemo(() => Object.fromEntries(organizationArray.map(o => [o.id, o])), [organizationArray]);
  const events = useMemo(() => Object.fromEntries(eventArray.map(e => [e.id, e])), [eventArray]);

  const { data: scene, isLoading: sceneLoading } = useNarrativeItem<Scene>('scenes', sceneId);

  const [analyzerLoading, setAnalyzerLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDialogManuallyClosed, setIsDialogManuallyClosed] = useState(false);
  const { t } = useTranslation();
  const { createSnapshot, isCreating: snapshotLoading } = useSceneSnapshot();
  const { hasFeature, isTokenDepleted } = useSubscription();

  const isGenerativeAiAllowed = hasFeature('is_generative_ai_allowed');
  const isAiAssistanceAllowed = hasFeature('ai_assistance_allowed');
  const navigate = useNavigate();

  const handleAnalyze = async () => {
    if (!novelId || !sceneId || !backend || !user) return;
    setAnalyzerLoading(true);
    setIsDialogManuallyClosed(false);
    try {
      await backend.ai.analyzeScene({ userId: user.uid, novelId, sceneId });
    } catch (error) {
      console.error('Analysis failed:', error);
    } finally {
      setAnalyzerLoading(false);
    }
  };

  const handleApplySuggestions = async () => {
    if (!novel.id || !scene?.proposal || !scene || !backend) return;
    setAnalyzerLoading(true);
    try {
      await backend.narrative.applySceneSplit(
        novel.ownerId,
        novel,
        scene,
        scene.proposal.proposedScenes,
        sceneArray
      );
      setIsDialogOpen(false);
      navigate({ to: '/scenes' });
    } catch (error) {
      console.error('Failed to apply suggestions:', error);
    } finally {
      setAnalyzerLoading(false);
    }
  };

  const handleCancelProposal = async () => {
    if (!novel.id || !scene || !backend) return;
    setAnalyzerLoading(true);
    try {
      await backend.narrative.cancelSceneProposal(novel.ownerId, novel, scene);
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Failed to cancel proposal:', error);
    } finally {
      setAnalyzerLoading(false);
    }
  };

  const handleDecideLater = () => {
    setIsDialogManuallyClosed(true);
    setIsDialogOpen(false);
  };

  const getEntityName = (id: string, entities: Record<string, AnySummary>) => {
    const entity = entities[id];
    if (!entity) return t('common.unknown');
    return 'name' in entity ? entity.name : 'title' in entity ? entity.title : t('common.unknown');
  };

  if (sceneLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!scene) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">{t('entity_view.scenes.not_found_title')}</Typography>
        <Button component={Link} to="/scenes" sx={{ mt: 2 }}>{t('entity_view.scenes.back')}</Button>
      </Box>
    );
  }

  return (
    <Page
      title={scene.name}
      description={t('entity_view.scenes.view_fallback')}
      breadcrumbs={[
        { label: t('entity_list.scenes.title'), href: '/scenes' },
        { label: scene.name }
      ]}
      actions={
        <Stack direction="row" spacing={2}>
          {isAiAssistanceAllowed && (
            <Tooltip title={isTokenDepleted && !scene.proposal ? t('entity_image_manager.common.tokens_depleted_tooltip') : ''} placement="top">
              <span>
                <Button
                  variant="outlined"
                  onClick={scene.proposal ? () => { setIsDialogOpen(true); setIsDialogManuallyClosed(false); } : handleAnalyze}
                  disabled={analyzerLoading || (isTokenDepleted && !scene.proposal)}
                  startIcon={analyzerLoading ? <CircularProgress size={18} /> : (scene.proposal ? (scene.proposal.verdict === 'pass' ? <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} /> : <SplitIcon sx={{ fontSize: 18 }} />) : <AnalyzerIcon sx={{ fontSize: 18 }} />)}
                  sx={{ borderRadius: 2 }}
                >
                  {scene.proposal ? (scene.proposal.verdict === 'pass' ? t('entity_view.scenes.analysis_passed') : t('entity_view.scenes.view_proposal')) : t('entity_view.scenes.analyze')}
                </Button>
              </span>
            </Tooltip>
          )}
          {isGenerativeAiAllowed && (
            <Tooltip title={isTokenDepleted ? t('entity_image_manager.common.tokens_depleted_tooltip') : ''} placement="top">
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => createSnapshot(scene)}
                  disabled={snapshotLoading || isTokenDepleted}
                  startIcon={snapshotLoading ? <CircularProgress size={18} /> : <AnalyzerIcon sx={{ fontSize: 18 }} />}
                  sx={{ borderRadius: 2 }}
                >
                  {t('entity_view.scenes.create_snapshot')}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button
            variant="contained"
            component={Link}
            to={`/scenes/edit/${scene.id}`}
            startIcon={<EditIcon sx={{ fontSize: 18 }} />}
            sx={{ borderRadius: 2, px: 3 }}
          >
            {t('entity_form.common.edit')}
          </Button>
        </Stack>
      }
    >

      <SceneDetailsCard
        scene={scene}
        locations={locations}
        characters={characters}
        organizations={organizations}
        events={events}
        getEntityName={getEntityName}
      />

      <ProposedSplitDialog
        open={isDialogOpen || (!!scene.proposal && !isDialogManuallyClosed)}
        onClose={handleDecideLater}
        proposal={scene.proposal}
        analyzerLoading={analyzerLoading}
        onCancelProposal={handleCancelProposal}
        onApplySuggestions={handleApplySuggestions}
        events={events}
        getEntityName={getEntityName}
      />
    </Page>
  );
}

function SceneViewPage() {
  return (
    <AuthGuard>
      <Suspense fallback={
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      }>
        <SceneViewContent />
      </Suspense>
    </AuthGuard>
  );
}

export default SceneViewPage;
